import NavigationService from '@/services/NavigationService/NavigationService';

class QueryParamsService {
  private static getParams() {
    return new URLSearchParams(location.search);
  }

  private static getNewUrl(params: URLSearchParams) {
    const searchParams = params.toString() ? `?${params.toString()}` : '';
    return `${location.pathname}${searchParams}`;
  }

  static constructSearchParamsFromRecord(obj: Record<string, unknown>) {
    const paramsWithValue = Object.entries(obj).reduce(
      (result, [key, value]) => {
        if (value == undefined) return result;

        return { ...result, [key]: String(value) };
      },
      {} as Record<string, string>,
    );

    return new URLSearchParams(paramsWithValue).toString();
  }

  static extractUrlWithoutOrigin(url: string | undefined): string {
    try {
      if (!url) return '';

      const origin = new URL(url).origin;
      if (!origin) return url;

      return url.replace(origin, '');
    } catch (error) {
      console.error('Error extracting URL without origin:', error);
      return '';
    }
  }

  static cleanUrlParams(url: string) {
    const [base, queryString] = url.split('?');

    if (!queryString) return url;

    const inputParams = new URLSearchParams(queryString);
    const outputParams = new URLSearchParams(queryString);

    for (const [key, value] of inputParams.entries()) {
      if (value === '' || value === 'null' || value === 'undefined') {
        outputParams.delete(key);
      }
    }

    const cleanedQuery = outputParams.toString();

    return cleanedQuery ? `${base}?${cleanedQuery}` : base;
  }

  static get(key: string, prefix?: string): string {
    const params = this.getParams();
    const queryKey = [prefix, key].filter(Boolean).join('-');

    return params.get(queryKey) || '';
  }

  static getAll(): Record<string, string> {
    const params = this.getParams();
    const allParams: Record<string, string> = {};

    params.forEach((value, key) => {
      allParams[key] = value;
    });

    return allParams;
  }

  static getAllByPrefix(prefix: string): Record<string, string> {
    const params = this.getParams();
    const filteredParams: Record<string, string> = {};

    params.forEach((value, key) => {
      if (key.startsWith(`${prefix}-`)) {
        const strippedKey = key.slice(prefix.length + 1); // Remove prefix and "-"
        filteredParams[strippedKey] = value;
      }
    });

    return filteredParams;
  }

  static getList(keys: string[], prefix?: string): Record<string, string> {
    const params = this.getParams();
    const result: Record<string, string> = {};

    keys.forEach((key) => {
      const queryKey = [prefix, key].filter(Boolean).join('-');
      if (params.has(queryKey)) result[key] = params.get(queryKey) || '';
    });

    return result;
  }

  static set(key: string, value: string | number | boolean, prefix?: string) {
    const params = this.getParams();
    const queryKey = [prefix, key].filter(Boolean).join('-');

    params.set(queryKey, value.toString());

    NavigationService.navigateTo(this.getNewUrl(params), { replace: true });
  }

  static setList(paramsList: { key: string; value: string | number | (string | number)[] }[], prefix?: string) {
    const params = this.getParams();

    paramsList.forEach(({ key, value }) => {
      const queryKey = [prefix, key].filter(Boolean).join('-');
      if (Array.isArray(value)) {
        params.set(queryKey, value.join(','));
      } else {
        params.set(queryKey, value.toString());
      }
    });

    NavigationService.navigateTo(this.getNewUrl(params), { replace: true });
  }

  static delete(key?: string, prefix?: string) {
    const params = this.getParams();

    if (key) {
      const queryKey = [prefix, key].filter(Boolean).join('-');
      params.delete(queryKey);
    } else {
      Array.from(params.keys()).forEach((queryKey) => {
        if (!prefix || queryKey.startsWith(`${prefix}-`)) {
          params.delete(queryKey);
        }
      });
    }

    NavigationService.navigateTo(this.getNewUrl(params), { replace: true });
  }

  static deleteAllByPrefix(prefixes: string[]) {
    const params = this.getParams();

    Array.from(params.keys()).forEach((queryKey) => {
      if (prefixes.find((prefix) => queryKey.startsWith(`${prefix}-`))) {
        params.delete(queryKey);
      }
    });

    NavigationService.navigateTo(this.getNewUrl(params), { replace: true });
  }
}

export default QueryParamsService;
